///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Redemption surface for the founding-supporter offer (funnel phase 4).
/// The offer itself is email-only: this card renders solely when the
/// ?offer=supporter deep link from the offer email is present, so the
/// discounted rate never appears in normal product UI and the public price
/// image never moves. Validity is decided server-side (send window +
/// no live subscription); checkout re-validates on redemption.
///
/// A sessionStorage marker keeps the card alive across the OAuth round
/// trip: a signed-out recipient clicks Sign in, leaves for the provider,
/// and finds the card waiting when they return.
///

import { Button } from '@/components/ui/button'
import { canPurchaseInApp } from '@/native'
import { setAuthOpen, setSupporterOfferOpen } from '@/redux/dialogSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useCreateCheckoutSessionMutation, useGetSupporterOfferQuery, useGetUserProfileQuery } from '@/redux/userApi'
import { logger } from '@/utils/LogDispatcher'
import { track } from '@/utils/analytics'
import { Gem, XIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

const cSessionMarker = 'eb_supporter_offer'

export function SupporterOfferCard() {
	const dispatch = useAppDispatch()
	const [searchParams, setSearchParams] = useSearchParams()
	const open = useAppSelector(state => state.dialog.supporterOfferOpen)
	const { data: userProfile, isLoading: profileLoading } = useGetUserProfileQuery()
	const signedIn = !profileLoading && userProfile !== undefined
	const { data: offer, isLoading: offerLoading } = useGetSupporterOfferQuery(undefined, { skip: !signedIn || !open })
	const [ createCheckoutSession, { isLoading: isRedeeming } ] = useCreateCheckoutSessionMutation()

	// Open from the email deep link; the sessionStorage marker survives the
	// OAuth redirect (dialog state doesn't), so the card reappears after
	// sign-in. Consume the URL param so refresh/share links stay clean.
	useEffect(() => {
		if (searchParams.get('offer') === 'supporter') {
			sessionStorage.setItem(cSessionMarker, '1')
			const params = new URLSearchParams(searchParams)
			params.delete('offer')
			setSearchParams(params, { replace: true })
		}
		if (sessionStorage.getItem(cSessionMarker) === '1') {
			dispatch(setSupporterOfferOpen(true))
		}
	}, [searchParams, setSearchParams, dispatch])

	// Funnel event: emitted once per appearance, not per re-render
	const capturedShown = useRef(false)
	useEffect(() => {
		if (!open || capturedShown.current) return
		capturedShown.current = true
		track('supporter_offer_viewed', { signed_in: signedIn })
	}, [open, signedIn])

	// The offer is a Stripe rate; store policy keeps web checkout out of
	// the native apps, so the card stays web-only (the email link opens
	// earthbrowser.com in a browser regardless)
	if (!open || !canPurchaseInApp()) return null

	const dismiss = () => {
		sessionStorage.removeItem(cSessionMarker)
		dispatch(setSupporterOfferOpen(false))
	}

	const handleRedeem = async () => {
		track('supporter_offer_checkout_started', {})
		try {
			const { url } = await createCheckoutSession({ offer: 'supporter' }).unwrap()
			sessionStorage.removeItem(cSessionMarker)
			window.location.href = url
		} catch (error) {
			logger.error(error)
		}
	}

	const expiresDate = offer?.expiresAt
		? new Date(offer.expiresAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
		: null

	// z-[45]: above the sign-in wall (z-40) so a walled recipient sees
	// their personal offer, below the auth dialog (z-50) it can open
	return (
		<div className="absolute inset-0 z-[45] flex items-center justify-center bg-black/50 backdrop-blur-md animate-in fade-in duration-500">
			<div className="relative mx-4 max-w-md rounded-2xl border border-white/20 bg-black/40 backdrop-blur-xl p-8 text-center shadow-2xl">
				<button
					onClick={dismiss}
					aria-label="Dismiss"
					className="absolute right-3 top-3 p-1.5 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
				>
					<XIcon className="h-4 w-4" />
				</button>

				<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/10">
					<Gem className="h-6 w-6 text-cyan-400" />
				</div>

				{!signedIn ? (
					<>
						<h2 className="text-xl font-semibold text-white">Your founding supporter rate</h2>
						<p className="mt-3 text-sm leading-relaxed text-white/70">
							Sign in with the account this offer was sent to, and
							we&apos;ll take it from there.
						</p>
						<Button
							onClick={() => dispatch(setAuthOpen(true))}
							className="mt-6 w-full rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
						>
							Sign in to continue
						</Button>
					</>
				) : offerLoading ? (
					<p className="text-sm text-white/70">Checking your offer…</p>
				) : offer?.available ? (
					<>
						<h2 className="text-xl font-semibold text-white">Your founding supporter rate</h2>
						<p className="mt-3 text-sm leading-relaxed text-white/70">
							Thank you for being one of our early explorers. Your
							personal rate is ready:
						</p>
						<p className="mt-4 text-white">
							<span className="text-3xl font-bold">{offer.price}</span>
							<span className="text-white/60">/year</span>
							{offer.regularPrice && (
								<span className="ml-2 text-sm text-white/40 line-through">{offer.regularPrice}</span>
							)}
						</p>
						<p className="mt-1 text-xs text-white/50">
							Grandfathered in for as long as you stay subscribed
						</p>
						<Button
							onClick={() => void handleRedeem()}
							disabled={isRedeeming}
							className="mt-6 w-full rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white disabled:hover:scale-100"
						>
							{isRedeeming ? "Preparing checkout…" : "Claim your rate"}
						</Button>
						{expiresDate && (
							<p className="mt-3 text-xs text-white/50">
								Available through {expiresDate}
							</p>
						)}
					</>
				) : (
					<>
						<h2 className="text-xl font-semibold text-white">This offer isn&apos;t available</h2>
						<p className="mt-3 text-sm leading-relaxed text-white/70">
							It may have expired, or it was sent to a different
							account. If you think this is a mistake, reach out at{" "}
							<a href="mailto:support@earthbrowser.com" className="text-blue-400 hover:text-blue-300 underline">
								support@earthbrowser.com
							</a>.
						</p>
						<Button
							onClick={dismiss}
							className="mt-6 w-full rounded-full border border-white/20 bg-white/10 backdrop-blur-md text-white shadow-lg transition-all duration-300 hover:bg-white/20"
						>
							Continue exploring
						</Button>
					</>
				)}
			</div>
		</div>
	)
}
